import Api from "@/api"
import { useLoading } from "@/composition/useLoading"
import type { IncidentLog } from "@/models"
import { useLoggedInUser } from "@/stores/coreDataStore"
import usePopups from "@/stores/popupsStore"
import { Duration } from "@/Temporal"
import { useEventListener, useTimeoutFn } from "@vueuse/core"
import { shallowRef } from "vue"
import { onBeforeRouteLeave, useRouter } from "vue-router"
import {
	Alert,
	ModalTitle,
	defineComponent,
	inferProps,
	refSetter,
	useCustomFormValidation,
	type ReactiveComponent,
} from "vue-utils"
import IncidentForm from "../shared/incident-form/IncidentForm"
import { CreateIncidentHandler } from "./CreateIncidentHandler"

interface Props {
	incident: IncidentLog
}

const CreateIncidentReminderTime = Duration.from({ minutes: 5 })

const CreateIncidentLog: ReactiveComponent<Props> = (props) => {
	const popups = usePopups()
	const loggedInUser = useLoggedInUser()
	const router = useRouter()
	const { runAction } = useLoading()

	const formRef = shallowRef<HTMLFormElement>()
	const validationHandler = useCustomFormValidation({
		form: formRef,
	})

	const handler = new CreateIncidentHandler({
		validationHandler,
		loggedInUser,
		incident: props.incident,
		createIncident: () => runAction(save()),
	})

	useTimeoutFn(() => {
		void popups.showAlertPopup({
			width: "42rem",
			content: () => (
				<Alert title="Reminder - Create Incident">
					You have not yet created this Incident.
					<br /> Please check all details and click "Create" at the bottom of the page to save this incident.
				</Alert>
			),
		})
	}, CreateIncidentReminderTime.total("milliseconds"))

	let hasSaved = false

	async function save() {
		const createdIncident = await Api.incidents.create(props.incident, handler.filesToUpload)

		hasSaved = true
		await router.replace(`/incidents/id/${createdIncident.id}`)
	}

	useEventListener("beforeunload", (e) => {
		if (!hasSaved) {
			e.preventDefault()
		}
	})

	onBeforeRouteLeave(async () => {
		if (hasSaved) {
			return true
		}
		return await popups.confirm(() => (
			<>
				<ModalTitle title="Confirm leave page" />
				<hr />
				<p>Are you sure you wish to leave without creating the incident?</p>
			</>
		))
	})

	return () => <IncidentForm handler={handler} setupForm={refSetter(formRef)} />
}

export default defineComponent(CreateIncidentLog, inferProps<Props>())
